import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { AiNotes, Field, PartyFields, sel } from '@/components/ewayBill/ManualDcForm';
import { PartyPick } from '@/components/ewayBill/PartyPick';

const KINDS = [['supplier', 'Goods from a supplier'], ['jw_return', 'Goods back from a job worker']];
const DOC_TYPES = ['Tax Invoice', 'Bill of Supply', 'Delivery Challan', 'Others'];
const LABELS = { legal_name: 'Sender name (supplier / job worker)' };
const PARTY_KEYS = ['legal_name', 'gstin', 'address1', 'address2', 'place', 'pincode', 'state'];
export const defaultUnit = (units) => (units || []).find((u) => u.is_default)?.id || units?.[0]?.id || null;

const MatchChip = ({ m }) => (m ? (
  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800" data-testid="inward-party-match">
    <CheckCircle2 className="w-3.5 h-3.5" />{m.source === 'jobber' ? 'Jobber Master' : 'Saved supplier'}: {m.name}
  </span>
) : null);

/** Inward bill header + sender (supplier / job worker) — AI-read or typed, every field editable; our unit is the receiver. */
export const InwardForm = ({ f, setF, states, units }) => {
  const setKind = (kind) => setF({ ...f, kind, document_type: kind === 'jw_return' ? 'Delivery Challan' : f.document_type === 'Delivery Challan' ? 'Tax Invoice' : f.document_type });
  const urpDoc = (gstin, doc) => (gstin === 'URP' && doc === 'Tax Invoice' ? 'Bill of Supply' : doc);
  const setParty = (k, v) => setF({ ...f, from: { ...f.from, [k]: v }, ...(k === 'gstin' ? { document_type: urpDoc(v, f.document_type) } : {}),
    ...(k === 'legal_name' || k === 'gstin' ? { party_ref: null, party_match: null } : {}) });
  const pick = (p) => setF({ ...f, from: Object.fromEntries(PARTY_KEYS.map((k) => [k, p[k] || ''])), document_type: urpDoc(p.gstin, f.document_type),
    party_ref: p.ref, party_match: { source: p.source, name: p.name } });
  return (
    <div className="space-y-3" data-testid="inward-form">
      <AiNotes notes={f.notes} />
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="What is coming in?">
          <select value={f.kind} onChange={(e) => setKind(e.target.value)} className={sel} data-testid="inward-kind">
            {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Bill type">
          <select value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })} className={sel} data-testid="inward-doc-type">
            {DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Received at (our unit)">
          <select value={f.to_unit_id || defaultUnit(units) || ''} onChange={(e) => setF({ ...f, to_unit_id: e.target.value })} className={sel} data-testid="inward-to-unit">
            {(units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Bill / DC number"><Input value={f.doc_no} onChange={(e) => setF({ ...f, doc_no: e.target.value })} maxLength={16} className="h-9 font-mono" data-testid="inward-doc-no" /></Field>
        <Field label="Bill / DC date"><Input type="date" value={f.doc_date} onChange={(e) => setF({ ...f, doc_date: e.target.value })} className="h-9" data-testid="inward-doc-date" /></Field>
      </div>
      <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50/30 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">From · {f.kind === 'jw_return' ? 'job worker' : 'supplier'}</span>
          <MatchChip m={f.party_match} />
          <span className="ml-auto" />
          <PartyPick onPick={pick} />
        </div>
        <PartyFields party={f.from} onChange={setParty} states={states} labels={LABELS} prefix="inward-party" />
      </div>
    </div>
  );
};

export default InwardForm;
